//  The merchant report spreadsheet. Column names match the hand-built "Customers with their
//  TMS software" sheet this report replaces, so anything already reading that sheet keeps
//  working: custom_price is gone (nothing in TowPay records one) and notes is new.

const DATE_FORMAT = "yyyy-mm-dd hh:mm:ss";

export const REPORT_COLUMNS = [
  { header: "account_name", key: "accountName", width: 42 },
  { header: "account_id", key: "accountId", width: 26 },
  { header: "account_email", key: "accountEmail", width: 34 },
  { header: "partner_name", key: "partnerName", width: 29 },
  { header: "tms", key: "tms", width: 20 },
  { header: "created_date", key: "createdDate", width: 20, type: "date" },
  { header: "tos_accepted_date", key: "tosAcceptedDate", width: 20, type: "date" },
  { header: "last_transaction_date", key: "lastTransactionDate", width: 22, type: "date" },
  { header: "is_enabled", key: "isEnabled", width: 12, type: "flag" },
  { header: "payments_enabled", key: "paymentsEnabled", width: 18, type: "flag" },
  { header: "payouts_enabled", key: "payoutsEnabled", width: 17, type: "flag" },
  { header: "ic_plus_plus", key: "icPlusPlus", width: 14, type: "flag" },
  { header: "effective_pricing", key: "effectivePricing", width: 60 },
  { header: "metadata", key: "metadata", width: 60, type: "json" },
  { header: "notes", key: "notes", width: 40 },
];

function cellValue(column, value) {
  if (value === null || value === undefined) return null;

  switch (column.type) {
    case "date": {
      //  A real Excel date, so the column sorts and filters as one. Excel has no time
      //  zone; ExcelJS writes the UTC wall clock, which is what the API sends.
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    }
    case "flag":
      //  1/0 like the original sheet, not TRUE/FALSE, so a SUM still counts them.
      return value ? 1 : 0;
    case "json":
      return Object.keys(value).length === 0 ? null : JSON.stringify(value);
    default:
      return value;
  }
}

/**
 * Fills a new workbook with the report rows.
 *
 * Takes the ExcelJS module as an argument so the page can load it only when someone
 * downloads, and the tests can hand it the real thing.
 *
 * @param {object} ExcelJS the exceljs module
 * @param {Array<object>} rows report rows from the API
 * @returns {object} the ExcelJS workbook
 */
export function buildMerchantReportWorkbook(ExcelJS, rows) {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "TowPay Merchant Management";

  const sheet = workbook.addWorksheet("Merchants", {
    views: [{ state: "frozen", ySplit: 1 }],
  });

  sheet.columns = REPORT_COLUMNS.map(({ header, key, width, type }) => ({
    header,
    key,
    width,
    style: type === "date" ? { numFmt: DATE_FORMAT } : undefined,
  }));
  sheet.getRow(1).font = { bold: true };

  (rows || []).forEach((row) => {
    sheet.addRow(
      Object.fromEntries(
        REPORT_COLUMNS.map((column) => [column.key, cellValue(column, row[column.key])])
      )
    );
  });

  sheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: REPORT_COLUMNS.length },
  };

  return workbook;
}

/**
 * @param {Date} at when the report was generated
 * @returns {string} e.g. towpay-merchants-2026-10-07.xlsx
 */
export function merchantReportFileName(at) {
  const date = at && !Number.isNaN(at.getTime()) ? at : new Date();
  return `towpay-merchants-${date.toISOString().slice(0, 10)}.xlsx`;
}

/**
 * Builds the spreadsheet in the browser and hands it to the user as a download.
 *
 * @param {Array<object>} rows report rows from the API
 * @param {string} generatedAt the run's completion time, for the file name
 */
export async function downloadMerchantReport(rows, generatedAt) {
  //  Loaded on click: ExcelJS is most of a megabyte, and only admins ever download.
  const ExcelJS = (await import("exceljs")).default;

  const buffer = await buildMerchantReportWorkbook(ExcelJS, rows).xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = merchantReportFileName(generatedAt ? new Date(generatedAt) : null);
  document.body.appendChild(link);
  link.click();
  link.remove();
  //  Deferred: Safari can drop a download whose URL is revoked in the same tick as the click.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
